// Deterministic geometry on the OHLCV candles already loaded by Chart.js.
// References for shape/duration: StockCharts ChartSchool; Lo, Mamaysky & Wang (2000);
// cup/handle: Fidelity/O'Neil. Other numerical thresholds are proposed, adjustable.
export const PATTERN_TYPES = [
  { id:'bullFlag', name:'Bullish flag', group:'Continuation' },
  { id:'bearFlag', name:'Bearish flag', group:'Continuation' },
  { id:'bullPennant', name:'Bullish pennant', group:'Continuation' },
  { id:'bearPennant', name:'Bearish pennant', group:'Continuation' },
  { id:'ascendingTriangle', name:'Ascending triangle', group:'Triangles' },
  { id:'descendingTriangle', name:'Descending triangle', group:'Triangles' },
  { id:'symTriangle', name:'Symmetrical triangle', group:'Triangles' },
  { id:'risingWedge', name:'Rising wedge', group:'Wedges & channels' },
  { id:'fallingWedge', name:'Falling wedge', group:'Wedges & channels' },
  { id:'risingChannel', name:'Rising channel', group:'Wedges & channels' },
  { id:'fallingChannel', name:'Falling channel', group:'Wedges & channels' },
  { id:'doubleTop', name:'Double top', group:'Reversals' },
  { id:'doubleBottom', name:'Double bottom', group:'Reversals' },
  { id:'headShoulders', name:'Head & shoulders', group:'Reversals' },
  { id:'inverseHeadShoulders', name:'Inverse H&S', group:'Reversals' },
  { id:'cupHandle', name:'Cup & handle', group:'Continuation' },
];
const common = (minBars,maxBars,tolerancePct=1.5) => ({minBars,maxBars,tolerancePct});
export const DEFAULT_PATTERN_SETTINGS = {
  enabled: false,
  pivotPct: 5,
  atrPeriod: 14,
  breakoutAtr: 0.25,
  confirmVolume: false,
  volumeMultiplier: 1.3,
  stages: 'both',
  visible: Object.fromEntries(PATTERN_TYPES.map(p=>[p.id,true])),
  rules: {
    bullFlag:{...common(5,20,2),poleATR:3,maxRetracePct:50,widthVarPct:20},
    bearFlag:{...common(5,20,2),poleATR:3,maxRetracePct:50,widthVarPct:20},
    bullPennant:{...common(5,20,2),poleATR:3,maxRetracePct:50,contractionPct:30},
    bearPennant:{...common(5,20,2),poleATR:3,maxRetracePct:50,contractionPct:30},
    ascendingTriangle:{...common(20,65),contractionPct:30},
    descendingTriangle:{...common(20,65),contractionPct:30},
    symTriangle:{...common(20,65),contractionPct:30},
    risingWedge:{...common(20,130,2),contractionPct:30},
    fallingWedge:{...common(20,130,2),contractionPct:30},
    risingChannel:{...common(20,130,2),widthVarPct:20},
    fallingChannel:{...common(20,130,2),widthVarPct:20},
    doubleTop:{...common(22,130),minSwingPct:10},
    doubleBottom:{...common(22,130),minSwingPct:10},
    headShoulders:{...common(20,150),headPct:2},
    inverseHeadShoulders:{...common(20,150),headPct:2},
    cupHandle:{...common(25,130,3),minDepthPct:12,maxDepthPct:33,maxHandlePct:33,minHandleBars:5,maxHandleBars:20},
  },
};
export const copyDefaults = () => JSON.parse(JSON.stringify(DEFAULT_PATTERN_SETTINGS));
const finite = n => typeof n === 'number' && Number.isFinite(n);
const pct = (a,b) => b ? 100*Math.abs(a-b)/Math.abs(b) : Infinity;
const between = (v,a,b) => v>=a && v<=b;
const close = (a,b,tolerance) => pct(a,b)<=tolerance;
const avg = xs => xs.reduce((a,b)=>a+b,0)/xs.length;
const value = (line,i) => line.a+line.b*i;

export function normaliseSettings(saved) {
  const d=copyDefaults();
  if (!saved || typeof saved !== 'object') return d;
  for (const k of ['enabled','confirmVolume']) if (typeof saved[k]==='boolean') d[k]=saved[k];
  if (['both','confirmed','developing','all'].includes(saved.stages)) d.stages=saved.stages;
  for (const [k,lo,hi] of [['pivotPct',0.5,20],['atrPeriod',2,100],['breakoutAtr',0,5],['volumeMultiplier',1,5]]) {
    if (finite(saved[k]) && between(saved[k],lo,hi)) d[k]=saved[k];
  }
  for (const p of PATTERN_TYPES) {
    if (typeof saved.visible?.[p.id]==='boolean') d.visible[p.id]=saved.visible[p.id];
    for (const k of Object.keys(d.rules[p.id])) {
      const n=saved.rules?.[p.id]?.[k];
      if (finite(n) && n>=0 && n<=500 && (k!=='minBars' && k!=='maxBars' || n>=3)) d.rules[p.id][k]=n;
    }
    if (d.rules[p.id].minBars>d.rules[p.id].maxBars) d.rules[p.id]={...DEFAULT_PATTERN_SETTINGS.rules[p.id]};
  }
  return d;
}
export const readPatternSettings = userId => {
  try {return normaliseSettings(JSON.parse(localStorage.getItem('chartPatterns:v1:'+userId))); }
  catch (_) {return copyDefaults();}
};

// Wilder true range; all values are real retrieved candles, no gap filling.
function atr(bars,period) {
  const a=[],p=Math.max(2,Math.round(period));
  let prev=0;
  bars.forEach((r,i)=>{
    const tr=Math.max(r[2]-r[3],Math.abs(r[2]-(bars[i-1]?.[4]??r[1])),Math.abs(r[3]-(bars[i-1]?.[4]??r[1])));
    prev=i===0?tr:(prev*(p-1)+tr)/p;
    a.push(prev);
  });
  return a;
}

// Local extrema confirmed only after the right-side candles exist.
function pivots(bars,span,threshold) {
  const raw=[];
  for(let i=span;i<bars.length-span;i++){
    const high=bars[i][2],low=bars[i][3];
    const isH=bars.slice(i-span,i+span+1).every((r,j)=>j===span || high>r[2]);
    const isL=bars.slice(i-span,i+span+1).every((r,j)=>j===span || low<r[3]);
    if (isH && !isL) raw.push({i,y:high,type:'H',confirmed:i+span});
    if (isL && !isH) raw.push({i,y:low,type:'L',confirmed:i+span});
  }
  const out=[];
  for(const p of raw){
    const last=out[out.length-1];
    if (!last) {out.push(p);continue;}
    if (last.type===p.type) {
      if ((p.type==='H' && p.y>last.y)||(p.type==='L'&&p.y<last.y)) out[out.length-1]=p;
    } else if(pct(p.y,last.y)>=threshold) out.push(p);
  }
  return out;
}
// A right-edge turning point uses an actual observed high/low, but is NOT confirmed.
 // It must only create developing candidates; unlike historic pivots it may move.
function latestSwing(bars,swings,threshold) {
  const last=swings[swings.length-1],n=bars.length-1;
  if (!last || n-last.i<2) return null;
  const type=last.type==='H'?'L':'H';
  let i=last.i+1;
  for(let j=i+1;j<=n;j++){
    if(type==='H' ? bars[j][2]>bars[i][2] : bars[j][3]<bars[i][3])i=j;
  }
  const y=type==='H'?bars[i][2]:bars[i][3];
  return pct(y,last.y)>=threshold ? {i,y,type,confirmed:n,provisional:true} : null;
}
function currentFormation(bars,atrValues,p,cfg) {
  const n=bars.length-1;
  if (value(p.upper,n)<=value(p.lower,n))return false;
  for(let i=p.end+1;i<=n;i++){
    const buffer=cfg.breakoutAtr*atrValues[i];
    if (bars[i][4]>value(p.upper,i)+buffer || bars[i][4]<value(p.lower,i)-buffer)return false;
  }
  const tolerance=cfg.breakoutAtr*atrValues[n];
  return bars[n][4]<=value(p.upper,n)+tolerance && bars[n][4]>=value(p.lower,n)-tolerance;
}
function regress(points) {
  const mx=avg(points.map(p=>p.i)),my=avg(points.map(p=>p.y));
  const den=points.reduce((s,p)=>s+(p.i-mx)**2,0);
  if(!den)return null;
  const b=points.reduce((s,p)=>s+(p.i-mx)*(p.y-my),0)/den;
  return {a:my-b*mx,b};
}
// Regress swing highs/lows for slope, then anchor at the outside of *observed*
// candle wicks. Require multiple genuine near-contacts with each boundary.
function fitEnvelope(bars,start,end,hs,ls,atrValues) {
  const topFit=regress(hs),bottomFit=regress(ls);
  if(!topFit||!bottomFit)return null;
  let topOffset=-Infinity,bottomOffset=Infinity;
  for(let i=start;i<=end;i++){
    topOffset=Math.max(topOffset,bars[i][2]-value(topFit,i));
    bottomOffset=Math.min(bottomOffset,bars[i][3]-value(bottomFit,i));
  }
  const upper={a:topFit.a+topOffset,b:topFit.b};
  const lower={a:bottomFit.a+bottomOffset,b:bottomFit.b};
  const touchDistance=Math.max(0.65*atrValues[end],0.005*bars[end][4]);
  const touchingHighs=hs.filter(p=>value(upper,p.i)-p.y<=touchDistance);
  const touchingLows=ls.filter(p=>p.y-value(lower,p.i)<=touchDistance);
  if(touchingHighs.length<2||touchingLows.length<2)return null;
  return {upper,lower};
}
const trend = (line,start,end,price) => 100*line.b*(end-start)/price;

// Shared exit-time check; completed pivot points cannot be used before confirmation.
function lifecycle(bars,atrValues,start,end,knownAt,upper,lower,expected,cfg) {
  const first=Math.max(end+1,knownAt);
  const last=Math.min(bars.length-1,end+Math.max(8,Math.round((end-start)*0.4)));
  for(let i=first;i<=last;i++){
    const buffer=cfg.breakoutAtr*atrValues[i];
    const up=bars[i][4]>value(upper,i)+buffer;
    const down=bars[i][4]<value(lower,i)-buffer;
    if(!up && !down)continue;
    const direction=up?'up':'down';
    if(cfg.confirmVolume){
      const prior=bars.slice(Math.max(0,i-20),i).map(r=>r[5]).filter(finite);
      if(prior.length<10 || bars[i][5]<avg(prior)*cfg.volumeMultiplier)continue;
    }
    return {stage:expected && expected!==direction?'failed':'confirmed',direction,index:i};
  }
  if(bars.length-1>last)return {stage:'failed',direction:null,index:last};
  return {stage:'developing',direction:null,index:bars.length-1};
}
function assemble(id,start,end,upper,lower,life,points=[]) {
  return {id,start,end,upper,lower,stage:life.stage,direction:life.direction,breakout:life.index,points};
}
function detectLines(bars,swings,atrValues,cfg) {
  const results=[];
  for(let j=3;j<swings.length;j++){
    for (const count of [4,5,6,7,8]){
      if(j+1<count)continue;
      const seq=swings.slice(j+1-count,j+1),start=seq[0].i,end=seq[seq.length-1].i;
      const hs=seq.filter(p=>p.type==='H'),ls=seq.filter(p=>p.type==='L');
      if(hs.length<2||ls.length<2)continue;
      const envelope=fitEnvelope(bars,start,end,hs,ls,atrValues);
      if(!envelope)continue;
      const {upper,lower}=envelope;
      const span=end-start,base=avg(seq.map(p=>p.y));
      const widthStart=value(upper,start)-value(lower,start);
      const widthEnd=value(upper,end)-value(lower,end);
      if(widthStart<=0||widthEnd<=0 || widthStart<atrValues[end]*0.35)continue;
      // Skip lines that don't actually fit observed swing points.
      const fit=Math.max(...seq.map(p=>pct(p.y,value(p.type==='H'?upper:lower,p.i))));
      if(fit>3)continue;
      // A formation must contain its closes, allowing small ATR deviations.
      let outside=0;
      for(let i=start;i<=end;i++){
        if(bars[i][4]>value(upper,i)+atrValues[i]*0.7||bars[i][4]<value(lower,i)-atrValues[i]*0.7)outside++;
      }
      if(outside/(span+1)>0.20)continue;
      const u=trend(upper,start,end,base),l=trend(lower,start,end,base);
      const contraction=100*(widthStart-widthEnd)/widthStart;
      const widthChange=100*Math.abs(widthEnd-widthStart)/widthStart;
      for(const t of PATTERN_TYPES.slice(0,11)){
        if(!cfg.visible[t.id])continue;
        const r=cfg.rules[t.id];
        if(!between(span,r.minBars,r.maxBars)||fit>r.tolerancePct)continue;
        const flatU=Math.abs(u)<=r.tolerancePct,flatL=Math.abs(l)<=r.tolerancePct;
        const risingU=u>r.tolerancePct,risingL=l>r.tolerancePct;
        const fallingU=u<-r.tolerancePct,fallingL=l<-r.tolerancePct;
        let passes=false,expected=null;
        switch(t.id){
          case 'ascendingTriangle': passes=flatU && risingL && contraction>=r.contractionPct;expected='up';break;
          case 'descendingTriangle': passes=flatL && fallingU && contraction>=r.contractionPct;expected='down';break;
          case 'symTriangle': passes=fallingU&&risingL&&contraction>=r.contractionPct;break;
          case 'risingWedge': passes=risingU&&risingL&&l>u&&contraction>=r.contractionPct;expected='down';break;
          case 'fallingWedge': passes=fallingU&&fallingL&&u<l&&contraction>=r.contractionPct;expected='up';break;
          case 'risingChannel': passes=risingU&&risingL&&widthChange<=r.widthVarPct;break;
          case 'fallingChannel': passes=fallingU&&fallingL&&widthChange<=r.widthVarPct;break;
          default: {
            const bull=t.id.startsWith('bull');
            const flag=t.id.endsWith('Flag');
            if (flag)passes=widthChange<=r.widthVarPct && (bull?(u<0&&l<0):(u>0&&l>0));
            else passes=contraction>=r.contractionPct && (bull?(u<0&&l>0):(u<0&&l>0));
            if(!passes)break;
            const from=Math.max(0,start-Math.max(10,span*2));
            const pole=bars[start][4]-bars[from][4];
            if (Math.abs(pole)<r.poleATR*atrValues[start] || (bull?pole<=0:pole>=0)) {passes=false;break;}
            const retrace=bull?bars.slice(start,end+1).reduce((x,r)=>Math.min(x,r[3]),Infinity):bars.slice(start,end+1).reduce((x,r)=>Math.max(x,r[2]),-Infinity);
            const reversal=bull?bars[start][4]-retrace:retrace-bars[start][4];
            passes=reversal/Math.abs(pole)*100<=r.maxRetracePct;
            expected=bull?'up':'down';
          }
        }
        if(!passes)continue;
        const life=lifecycle(bars,atrValues,start,end,seq[seq.length-1].confirmed,upper,lower,expected,cfg);
        results.push(assemble(t.id,start,end,upper,lower,life,seq));
      }
    }
  }
  return results;
}
function detectTurns(bars,swings,atrValues,cfg) {
  const out=[];
  for(let j=2;j<swings.length;j++){
    const a=swings[j-2],b=swings[j-1],c=swings[j],span=c.i-a.i;
    for(const type of ['doubleTop','doubleBottom']){
      if(!cfg.visible[type])continue;
      const r=cfg.rules[type],top=type==='doubleTop';
      if(!between(span,r.minBars,r.maxBars)||a.type!==(top?'H':'L')||b.type===a.type||c.type!==a.type)continue;
      const midpoint=(a.y+c.y)/2;
      if(pct(a.y,midpoint)>r.tolerancePct||pct(c.y,midpoint)>r.tolerancePct)continue;
      const swingPct=100*Math.abs(midpoint-b.y)/midpoint;
      if(swingPct<r.minSwingPct)continue;
      const line={a:midpoint,b:0},neck={a:b.y,b:0};
      const upper=top?line:neck,lower=top?neck:line;
      const life=lifecycle(bars,atrValues,a.i,c.i,c.confirmed,upper,lower,top?'down':'up',cfg);
      out.push(assemble(type,a.i,c.i,upper,lower,life,[a,b,c]));
    }
    if(j>=4){
    const p=swings.slice(j-4,j+1),[s1,n1,h,n2,s2]=p,span5=s2.i-s1.i;
    for(const type of ['headShoulders','inverseHeadShoulders']){
      if(!cfg.visible[type])continue;
      const r=cfg.rules[type],top=type==='headShoulders';
      if(!between(span5,r.minBars,r.maxBars)||s1.type!==(top?'H':'L')||h.type!==s1.type||s2.type!==s1.type||n1.type===s1.type||n2.type===s1.type)continue;
      const shoulders=(s1.y+s2.y)/2;
      if(pct(s1.y,shoulders)>r.tolerancePct||pct(s2.y,shoulders)>r.tolerancePct)continue;
      if(top?(h.y<shoulders*(1+r.headPct/100)):(h.y>shoulders*(1-r.headPct/100)))continue;
      const neck=regress([n1,n2]),shoulder=regress([s1,s2]);
      if(!neck||!shoulder)continue;
      const upper=top?shoulder:neck,lower=top?neck:shoulder;
      const life=lifecycle(bars,atrValues,s1.i,s2.i,s2.confirmed,upper,lower,top?'down':'up',cfg);
      out.push(assemble(type,s1.i,s2.i,upper,lower,life,p));
    }
    }
    if(j<3 || !cfg.visible.cupHandle)continue;
    const cupSeq=swings.slice(j-3,j+1),[rim1,trough,rim2,handle]=cupSeq;
    const r=cfg.rules.cupHandle,spanCup=rim2.i-rim1.i,spanHandle=handle.i-rim2.i;
    if(rim1.type!=='H'||trough.type!=='L'||rim2.type!=='H'||handle.type!=='L')continue;
    if(!between(spanCup,r.minBars,r.maxBars)||!between(spanHandle,r.minHandleBars,r.maxHandleBars))continue;
    if(!close(rim1.y,rim2.y,r.tolerancePct))continue;
    const depth=100*(rim1.y-trough.y)/rim1.y;
    if(!between(depth,r.minDepthPct,r.maxDepthPct))continue;
    if((rim2.y-handle.y)/(rim1.y-trough.y)*100>r.maxHandlePct)continue;
    // Require a genuinely rounded trough rather than a one-bar V.
    const bottom=bars.slice(rim1.i,rim2.i+1).filter(b=>b[3]<=trough.y+(rim1.y-trough.y)*0.2);
    if(bottom.length/spanCup<0.15)continue;
    const upper={a:(rim1.y+rim2.y)/2,b:0},lower={a:handle.y,b:0};
    const life=lifecycle(bars,atrValues,rim1.i,handle.i,handle.confirmed,upper,lower,'up',cfg);
    out.push(assemble('cupHandle',rim1.i,handle.i,upper,lower,life,cupSeq));
  }
  return out;
}
// Examine recent real high/low extrema even before alternating ZigZag pivots
// are confirmed. Only returns *developing*, never retrospectively confirmed.
function detectCurrentWedges(bars,atrValues,cfg) {
  const n=bars.length-1,out=[];
  for(const id of ['fallingWedge','risingWedge']){
    if(!cfg.visible[id])continue;
    const rule=cfg.rules[id],falling=id==='fallingWedge';
    for(const length of [20,30,45,65,90,120]){
      if(length<rule.minBars||length>rule.maxBars||length>bars.length)continue;
      const start=n-length+1,step=Math.max(3,Math.floor(length/6));
      const highs=[],lows=[];
      for(let first=start;first<=n;first+=step){
        let high=first,low=first;
        for(let i=first+1;i<=Math.min(n,first+step-1);i++){
          if(bars[i][2]>bars[high][2])high=i;
          if(bars[i][3]<bars[low][3])low=i;
        }
        highs.push({i:high,y:bars[high][2],type:'H'});
        lows.push({i:low,y:bars[low][3],type:'L'});
      }
      // Do not refit a broken pattern around today's breakout and call it
      // developing. The latest close must respect yesterday's boundaries.
      const prior=fitEnvelope(bars,start,n-1,
        highs.filter(p=>p.i<n),lows.filter(p=>p.i<n),atrValues);
      if(!prior)continue;
      const margin=cfg.breakoutAtr*atrValues[n];
      if(bars[n][4]>value(prior.upper,n)+margin ||
         bars[n][4]<value(prior.lower,n)-margin)continue;
      const envelope=fitEnvelope(bars,start,n,highs,lows,atrValues);
      if(!envelope)continue;
      const {upper,lower}=envelope,price=bars[n][4];
      const firstWidth=value(upper,start)-value(lower,start);
      const lastWidth=value(upper,n)-value(lower,n);
      if(firstWidth<atrValues[n]||lastWidth<=0 ||
         100*(firstWidth-lastWidth)/firstWidth<rule.contractionPct)continue;
      const topSlope=trend(upper,start,n,price),bottomSlope=trend(lower,start,n,price);
      if(falling
        ? !(topSlope<-rule.tolerancePct && bottomSlope<-rule.tolerancePct && topSlope<bottomSlope)
        : !(topSlope>rule.tolerancePct && bottomSlope>rule.tolerancePct && bottomSlope>topSlope))continue;
      const fit=Math.max(...highs.map(p=>pct(value(upper,p.i),p.y)),
                         ...lows.map(p=>pct(value(lower,p.i),p.y)));
      if(fit>rule.tolerancePct)continue;
      out.push({...assemble(id,start,n,upper,lower,{stage:'developing',direction:null,index:n},
        [...highs,...lows]),provisional:true});
    }
  }
  return out;
}
export function detectPatterns(ohlcv,config) {
  const cfg=normaliseSettings(config);
  if(!cfg.enabled||!Array.isArray(ohlcv)||ohlcv.length<35)return [];
  const bars=ohlcv.filter(r=>Array.isArray(r)&&r.length>=5&&r.slice(0,5).every(finite)&&r[1]>0&&r[2]>=r[3]&&r[4]>0);
  if(bars.length<35)return [];
  const atrValues=atr(bars,cfg.atrPeriod);
  const threshold=Math.max(0.5,cfg.pivotPct/3);
  const local=pivots(bars,2,threshold),major=pivots(bars,3,cfg.pivotPct);
  const matches=[...detectLines(bars,local,atrValues,cfg),...detectTurns(bars,major,atrValues,cfg)];
  // Early recognition at the right edge: one unconfirmed swing extracted from
  // real candles. Never promote it to a historical confirmed breakout.
  for(const [swings,threshold,detector] of [
    [local,Math.max(0.3,cfg.pivotPct/6),detectLines],
    [major,Math.max(0.5,cfg.pivotPct/2),detectTurns],
  ]){
    const candidate=latestSwing(bars,swings,threshold);
    if(!candidate)continue;
    const recent=detector(bars,[...swings,candidate],atrValues,cfg)
      .filter(p=>p.end===candidate.i && currentFormation(bars,atrValues,p,cfg));
    matches.push(...recent.map(p=>({...p,stage:'developing',direction:null,breakout:bars.length-1,provisional:true})));
  }
  matches.push(...detectCurrentWedges(bars,atrValues,cfg));
  const visible=matches.filter(p=>
    cfg.stages==='all' ||
    (cfg.stages==='developing' ? p.stage==='developing'
      : cfg.stages==='confirmed' ? p.stage==='confirmed' : p.stage!=='failed'));
  // Prefer more recently confirmed candidates. Coalesce near-duplicates from different pivot counts.
  visible.sort((a,b)=>(b.stage==='developing')-(a.stage==='developing') || b.breakout-a.breakout || b.end-a.end);
  const kept=[];
  for(const p of visible){
    if(kept.some(x=>x.id===p.id && Math.abs(x.end-p.end)<Math.max(5,(p.end-p.start)*0.35)))continue;
    kept.push(p);

  }
  return kept.map(p=>({...p,startTs:bars[p.start][0],endTs:bars[p.end][0],breakoutTs:bars[p.breakout][0]}));
}
export function drawPatternOverlays(chart,patterns) {
  if(chart._autoPatternGroup){chart._autoPatternGroup.destroy();chart._autoPatternGroup=null;}
  if(chart._autoPatternClip){chart._autoPatternClip.destroy();chart._autoPatternClip=null;}
  if(!patterns.length)return;
  const xAxis=chart.xAxis[0],yAxis=chart.yAxis[0];
  if(!xAxis||!yAxis)return;
  const limits=xAxis.getExtremes(),left=limits.min,right=limits.max;
  const clip=chart.renderer.clipRect(chart.plotLeft,chart.plotTop,chart.plotWidth,chart.plotHeight);
  const group=chart.renderer.g('automatic-patterns').attr({zIndex:6}).css({pointerEvents:'none'}).add().clip(clip);
  chart._autoPatternGroup=group;
  chart._autoPatternClip=clip;
  const colorFor=p=>p.stage==='failed'?'#8b8b8b':p.direction==='down'||p.id.startsWith('bear')||p.id==='risingWedge'||p.id==='doubleTop'||p.id==='headShoulders'?'#c0392b':'#18856b';
  let shown=0;
  for(const p of patterns){
    if(p.startTs>right||p.breakoutTs<left)continue;
    if(++shown>48)break;
    const color=colorFor(p);
    for(const line of [p.upper,p.lower]){
      const x1=p.startTs,x2=p.breakoutTs;
      const coords=[xAxis.toPixels(x1),yAxis.toPixels(value(line,p.start)),xAxis.toPixels(x2),yAxis.toPixels(value(line,p.breakout))];
      if(coords.every(finite))chart.renderer.path(['M',coords[0],coords[1],'L',coords[2],coords[3]])
        .attr({stroke:color,'stroke-width':1.6,dashstyle:p.stage==='developing'?'ShortDash':'Solid',opacity:0.9}).add(group);
    }
    const type=PATTERN_TYPES.find(t=>t.id===p.id),x=xAxis.toPixels(p.endTs);
    const y=yAxis.toPixels(value(p.upper,p.end));
    if(!finite(x)||!finite(y))continue;
    const label=type.name+' · '+(p.stage==='confirmed'?'breakout '+p.direction:p.stage+(p.provisional?' (tentative)':''));
    chart.renderer.label(label,Math.max(chart.plotLeft+4,Math.min(x,chart.plotLeft+chart.plotWidth-145)),Math.max(chart.plotTop+2,Math.min(y-22,chart.plotTop+chart.plotHeight-22)),'rect')
      .attr({fill:'#ffffff','fill-opacity':0.9,stroke:color,'stroke-width':1,padding:3,r:3})
      .css({fontSize:'10px',fontWeight:'600',color}).add(group);
  }
}
